'use client';

import * as React from 'react';
import { getPropertyPriceDisplay } from '@federa/components/lib/property-card-display';
import { getCountdownDisplay } from '@federa/components/domain-cards/countdown-display';
import { resolveCardPriceInfo } from '@federa/components/property/property-card';
import { FULL_HEIGHT_STYLE } from '@federa/const/style-size-presets';
import { usePropertyCardState } from './PropertyCard.hooks.ts';
import { CardContent } from './PropertyCard-helpers.tsx';
import { getCardStyles } from './property-card-styles.ts';
import type { PropertyCardProps } from '@federa/types/ui-property-card';

export const PropertyCard = React.forwardRef<HTMLDivElement, PropertyCardProps>(
	({ property, onClick, onShare, onSave, isSaved = false, hoverable = true, href, onNavigateAway, sentinelImageSrc, eagerImage = false }, ref) => {
		const state = usePropertyCardState({
			property,
			onClick,
			onShare,
			onSave,
			hoverable,
		});
		const priceInfo = resolveCardPriceInfo(property);
		const { label: priceLabel, amount: priceAmount } = getPropertyPriceDisplay({
			...priceInfo,
			remainingSeconds: state.remainingSeconds,
		});
		const countdownInfo = getCountdownDisplay(state.remainingSeconds);
		const cardStyles = getCardStyles(state.isHovered, hoverable, !!onClick);

		const { handleCardClick } = state;
		const handleLinkClick = React.useCallback(
			(e: React.MouseEvent) => {
				if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
				e.preventDefault();
				// Host-supplied navigation hook (explore wires scroll restoration; other hosts omit it). No-ops when the prop is absent.
				onNavigateAway?.();
				handleCardClick();
			},
			[handleCardClick, onNavigateAway],
		);
		const shared = {
			ref: ref as React.Ref<HTMLAnchorElement & HTMLDivElement>,
			style: {
				...cardStyles,
				textDecoration: 'none',
				color: 'inherit',
				...FULL_HEIGHT_STYLE,
			} as const,
			onMouseEnter: state.handleMouseEnter,
			onMouseLeave: state.handleMouseLeave,
			// NO `aria-label`: the card's own address line is the accessible name. A label spelled "Property at <address>"
			// did not contain the visible text verbatim, so a voice-control user reading the card aloud could not activate it
			// (Lighthouse `label-content-name-mismatch` on every card, dev 2026-09-17).
		};
		const content = (
			<CardContent
				property={property}
				sentinelImageSrc={sentinelImageSrc}
				eagerImage={eagerImage}
				state={state}
				actions={{ onSave, onShare, isSaved }}
				display={{
					priceLabel,
					priceAmount,
					showCountdown: countdownInfo.showCountdown,
					countdownLabel: countdownInfo.countdownLabel,
					countdownColor: countdownInfo.countdownColor,
				}}
			/>
		);

		if (href)
			return (
				<a {...shared} href={href} onClick={handleLinkClick}>
					{content}
				</a>
			);

		return (
			<div
				{...shared}
				onClick={handleCardClick}
				onKeyDown={onClick ? state.handleKeyDown : undefined}
				role={onClick ? 'button' : undefined}
				tabIndex={onClick ? 0 : undefined}
			>
				{content}
			</div>
		);
	},
);

PropertyCard.displayName = 'PropertyCard';
